import { useCallback, useMemo, useSyncExternalStore } from "react";
import { useQuery as useZeroQuery } from "@rocicorp/zero/react";
import { appQueries } from "@production-hub/shared";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiJson } from "@/lib/api-client";
import { zeroErrorMessage } from "@/lib/zero-error";
import { useAppSetting } from "@/hooks/useAppSettings";
import { useEquipmentTags } from "@/hooks/useEquipmentTags";
import {
  SIM_DEVICE_TAGS_SETTING,
  expandTagIds,
  imeiTac,
  countryName,
  normalizeImei,
  profileCountries,
  resolveSimProfile,
  type ActionResponse,
  type PreviewEntry,
  type SimCard,
  type SimDeviceTagsSetting,
  type SimProfile,
} from "@/lib/sims";

type ZeroEquipment = { id: string; name: string; shortName?: string | null };
type ZeroInstance = {
  id: string;
  identifier: string;
  equipmentID: string;
  status?: string;
  equipment?: ZeroEquipment | null;
};
type ZeroSimRow = {
  id: string;
  iccid: string;
  provider: SimCard["provider"];
  source: SimCard["source"];
  msisdn?: string | null;
  imsi?: string | null;
  imei?: string | null;
  status?: string | null;
  statusLabel?: string | null;
  ratePlan?: string | null;
  equipmentInstanceID?: string | null;
  slot?: number | null;
  label?: string | null;
  note?: string | null;
  simetricSegment1?: string | null;
  simetricSegment2?: string | null;
  simetricSegment3?: string | null;
  usageCycle?: number | null;
  ctdDataMb?: number | null;
  usageUpdatedAt?: number | null;
  lastSyncedAt?: number | null;
  missingSince?: number | null;
  profileID?: string | null;
  instance?: ZeroInstance | null;
};

type ZeroProfileRow = {
  id: string;
  provider: SimProfile["provider"];
  name: string;
  isOpen: boolean;
  ratePlan?: string | null;
  notes?: string | null;
  networks?: {
    countryCode: string;
    networks: string[] | null;
    note?: string | null;
  }[];
};

export function deviceDisplayName(instance: ZeroInstance | null | undefined) {
  if (!instance) return null;
  const equipment = instance.equipment;
  const name = equipment?.shortName?.trim() || equipment?.name || "Device";
  return `${name} ${instance.identifier}`.trim();
}

function toSimCard(row: ZeroSimRow, profiles: SimProfile[]): SimCard {
  const manualProfileID = row.profileID ?? null;
  const ratePlan = row.ratePlan ?? null;
  const resolved = resolveSimProfile(
    { provider: row.provider, ratePlan, manualProfileID },
    profiles,
  );
  return {
    id: row.id,
    iccid: row.iccid,
    provider: row.provider,
    source: row.source,
    msisdn: row.msisdn ?? null,
    imsi: row.imsi ?? null,
    imei: row.imei ?? null,
    status: row.status ?? null,
    statusLabel: row.statusLabel ?? null,
    ratePlan: row.ratePlan ?? null,
    equipmentInstanceID: row.equipmentInstanceID ?? null,
    slot: row.slot ?? null,
    label: row.label ?? null,
    note: row.note ?? null,
    simetricSegment1: row.simetricSegment1 ?? null,
    simetricSegment2: row.simetricSegment2 ?? null,
    simetricSegment3: row.simetricSegment3 ?? null,
    usageCycle: row.usageCycle ?? null,
    ctdDataMb: row.ctdDataMb ?? null,
    usageUpdatedAt: row.usageUpdatedAt ?? null,
    lastSyncedAt: row.lastSyncedAt ?? null,
    missingSince: row.missingSince ?? null,
    deviceName: deviceDisplayName(row.instance),
    equipmentID: row.instance?.equipmentID ?? null,
    manualProfileID,
    profileID: resolved?.profile.id ?? null,
    profileName: resolved?.profile.name ?? null,
    profileVia: resolved?.via ?? null,
  };
}

function zeroState(details: { type: string; error?: unknown }, data: unknown) {
  return {
    isLoading: details.type !== "complete" && !data,
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error as never))
        : null,
  };
}

/** All SIM profiles with their network preferences per country. */
export function useSimProfiles() {
  const [rows, details] = useZeroQuery(appQueries.sims.profiles());
  const data = useMemo(
    () =>
      ((rows ?? []) as unknown as ZeroProfileRow[]).map(
        (row): SimProfile => ({
          id: row.id,
          provider: row.provider,
          name: row.name,
          isOpen: row.isOpen,
          ratePlan: row.ratePlan ?? null,
          notes: row.notes ?? null,
          networks: (row.networks ?? []).map((n) => ({
            countryCode: n.countryCode,
            networks: Array.isArray(n.networks) ? n.networks : [],
            note: n.note ?? null,
          })),
        }),
      ),
    [rows],
  );
  return { data, ...zeroState(details, rows) };
}

const COUNTRY_KEY = "sims.networkCountry";
const COUNTRY_EVENT = "sims-network-country";

function subscribeCountry(onChange: () => void) {
  window.addEventListener(COUNTRY_EVENT, onChange);
  window.addEventListener("storage", onChange);
  return () => {
    window.removeEventListener(COUNTRY_EVENT, onChange);
    window.removeEventListener("storage", onChange);
  };
}

function readCountry() {
  try {
    return localStorage.getItem(COUNTRY_KEY);
  } catch {
    return null;
  }
}

/**
 * Country whose networks the SIM lists show. Remembered in the browser and
 * shared by every SIM list on the page.
 */
export function useSimNetworkCountry() {
  const country = useSyncExternalStore(
    subscribeCountry,
    readCountry,
    () => null,
  );
  const setCountry = useCallback((code: string | null) => {
    try {
      if (code) localStorage.setItem(COUNTRY_KEY, code);
      else localStorage.removeItem(COUNTRY_KEY);
    } catch {
      // Storage unavailable: the choice is not remembered.
    }
    window.dispatchEvent(new Event(COUNTRY_EVENT));
  }, []);
  return [country, setCountry] as const;
}

/** Countries with a network preference in any profile, by name. */
export function useSimProfileCountries() {
  const { data: profiles } = useSimProfiles();
  return useMemo(
    () =>
      profileCountries(profiles)
        .map((code) => ({ code, name: countryName(code) }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [profiles],
  );
}

export function useSimCards() {
  const [rows, details] = useZeroQuery(appQueries.sims.list());
  const { data: profiles } = useSimProfiles();
  const data = useMemo(
    () =>
      ((rows ?? []) as unknown as ZeroSimRow[]).map((row) =>
        toSimCard(row, profiles),
      ),
    [rows, profiles],
  );
  return { data, ...zeroState(details, rows) };
}

/** One SIM by id (with its device), independent of any list it is shown in. */
export function useSimCard(id: string | null) {
  const [row, details] = useZeroQuery(
    appQueries.sims.byId({ id: id ?? "__none__" }),
  );
  const { data: profiles } = useSimProfiles();
  const data = useMemo(
    () =>
      id && row ? toSimCard(row as unknown as ZeroSimRow, profiles) : null,
    [id, row, profiles],
  );
  return { data, ...zeroState(details, row) };
}

export function useInstanceSims(instanceID: string | undefined) {
  const [rows, details] = useZeroQuery(
    appQueries.sims.byInstance({ instanceID: instanceID ?? "__none__" }),
  );
  const { data: profiles } = useSimProfiles();
  const data = useMemo(
    () =>
      ((rows ?? []) as unknown as ZeroSimRow[]).map((row) =>
        toSimCard(row, profiles),
      ),
    [rows, profiles],
  );
  return { data, ...zeroState(details, rows) };
}

export function useInstancesSimCounts(instanceIDs: string[]) {
  const [rows] = useZeroQuery(
    appQueries.sims.byInstances({
      instanceIDs: instanceIDs.length > 0 ? instanceIDs : ["__none__"],
    }),
  );
  return useMemo(() => {
    const counts = new Map<string, number>();
    for (const row of (rows ?? []) as unknown as ZeroSimRow[]) {
      if (!row.equipmentInstanceID) continue;
      counts.set(
        row.equipmentInstanceID,
        (counts.get(row.equipmentInstanceID) ?? 0) + 1,
      );
    }
    return counts;
  }, [rows]);
}

/** Tag ids from the `sim_device_tags` setting, expanded with descendants. */
export function useSimDeviceTagIds() {
  const { data: setting, isLoading: settingLoading } =
    useAppSetting<SimDeviceTagsSetting>(SIM_DEVICE_TAGS_SETTING);
  const { data: tags, isLoading: tagsLoading } = useEquipmentTags();
  const configured = Array.isArray(setting?.tagIds) ? setting!.tagIds : [];
  const expanded = useMemo(
    () => (tags ? expandTagIds(tags, configured) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tags, configured.join(",")],
  );
  return {
    configuredTagIds: configured,
    tagIds: expanded,
    isLoading: settingLoading || tagsLoading,
  };
}

export type SimDevice = {
  id: string;
  identifier: string;
  equipmentID: string;
  name: string;
};

/** Equipment instances whose equipment carries one of the SIM device tags. */
export function useSimDevices() {
  const { tagIds, isLoading: tagsLoading } = useSimDeviceTagIds();
  const [rows, details] = useZeroQuery(
    appQueries.sims.eligibleDevices({ tagIDs: tagIds }),
  );
  const data = useMemo(
    () =>
      ((rows ?? []) as unknown as ZeroInstance[])
        .map(
          (row): SimDevice => ({
            id: row.id,
            identifier: row.identifier,
            equipmentID: row.equipmentID,
            name: deviceDisplayName(row) ?? row.identifier,
          }),
        )
        .sort((a, b) => a.name.localeCompare(b.name)),
    [rows],
  );
  return {
    data,
    tagIds,
    isLoading:
      tagsLoading ||
      (tagIds.length > 0 && details.type !== "complete" && !rows),
  };
}

export type ImeiModel = { tac: string; brand: string; name: string };

/** Normalised IMEI of a SIM's last modem plus its make/model from the TAC table. */
export function useImeiModel(rawImei: string | null | undefined) {
  const imei = normalizeImei(rawImei);
  const tac = imeiTac(rawImei);
  const [rows, details] = useZeroQuery(
    appQueries.sims.imeiModels({ tacs: [tac ?? "__none__"] }),
  );
  const model = ((rows ?? []) as unknown as ImeiModel[])[0] ?? null;
  return {
    imei,
    tac,
    model,
    isLoading: !!tac && details.type !== "complete" && !model,
  };
}

/** Modem make/model by TAC for many IMEIs at once (e.g. a page of SIMs). */
export function useImeiModels(rawImeis: (string | null | undefined)[]) {
  const key = Array.from(
    new Set(rawImeis.map(imeiTac).filter((tac): tac is string => !!tac)),
  )
    .sort()
    .join(",");
  const tacs = useMemo(() => (key ? key.split(",") : ["__none__"]), [key]);
  const [rows] = useZeroQuery(appQueries.sims.imeiModels({ tacs }));
  return useMemo(
    () =>
      new Map(((rows ?? []) as unknown as ImeiModel[]).map((m) => [m.tac, m])),
    [rows],
  );
}

export type SimMonthlyRow = {
  simCardID: string;
  cycleMonth: number;
  dataMb: number;
};
export type SimDailyRow = {
  simCardID: string;
  day: number;
  dataMb: number;
  isEstimated: boolean;
};

export function useSimUsageMonthly(since: number) {
  const [rows, details] = useZeroQuery(appQueries.sims.usageMonthly({ since }));
  return {
    data: (rows ?? []) as unknown as SimMonthlyRow[],
    ...zeroState(details, rows),
  };
}

export function useSimUsageForSims(simCardIDs: string[], dailySince: number) {
  const ids = simCardIDs.length > 0 ? simCardIDs : ["__none__"];
  const [monthly] = useZeroQuery(
    appQueries.sims.usageMonthlyBySims({ simCardIDs: ids }),
  );
  const [daily] = useZeroQuery(
    appQueries.sims.usageDailyBySims({ simCardIDs: ids, since: dailySince }),
  );
  return {
    monthly: (monthly ?? []) as unknown as SimMonthlyRow[],
    daily: (daily ?? []) as unknown as SimDailyRow[],
  };
}

export type SimActionItem = {
  id: string;
  batchID: string;
  simCardID: string;
  operation: "activate" | "suspend" | "deactivate" | "set_segments";
  state:
    | "queued"
    | "submitted"
    | "completed"
    | "failed"
    | "skipped"
    | "unknown";
  skipReason?: string | null;
  lastError?: string | null;
  simCard?: { iccid: string } | null;
};

export type SimActionBatch = {
  id: string;
  kind: "activate" | "deactivate" | "label";
  status: "queued" | "running" | "completed" | "partially_failed" | "failed";
  summary: Record<string, number> | null;
  createdAt?: number | null;
  items?: SimActionItem[];
  requester?: { firstName: string; lastName: string } | null;
  instance?: ZeroInstance | null;
};

export function useRecentSimBatches(limit = 10) {
  const [rows, details] = useZeroQuery(
    appQueries.sims.recentBatches({ limit }),
  );
  return {
    data: (rows ?? []) as unknown as SimActionBatch[],
    ...zeroState(details, rows),
  };
}

/** SIM ids with a queued/submitted action (pending spinner). */
export function usePendingSimIds() {
  const [rows] = useZeroQuery(appQueries.sims.openItems());
  return useMemo(
    () =>
      new Set(
        ((rows ?? []) as unknown as SimActionItem[]).map((r) => r.simCardID),
      ),
    [rows],
  );
}

// ---------------------------------------------------------------------------
// API mutations
// ---------------------------------------------------------------------------

const post = <T,>(url: string, body: unknown) =>
  apiJson<T>(url, { method: "POST", body: JSON.stringify(body) });

export function previewSimAction(input: {
  simIds?: string[];
  instanceId?: string;
  action: "activate" | "deactivate";
}) {
  return input.instanceId
    ? post<ActionResponse>(`/api/sims/devices/${input.instanceId}/actions`, {
        action: input.action,
        dryRun: true,
      })
    : post<ActionResponse>("/api/sims/actions", {
        simIds: input.simIds,
        action: input.action,
        dryRun: true,
      });
}

export function useSimAction() {
  return useMutation({
    mutationFn: (input: {
      simIds?: string[];
      instanceId?: string;
      action: "activate" | "deactivate";
    }) =>
      input.instanceId
        ? post<ActionResponse>(
            `/api/sims/devices/${input.instanceId}/actions`,
            {
              action: input.action,
            },
          )
        : post<ActionResponse>("/api/sims/actions", {
            simIds: input.simIds,
            action: input.action,
          }),
    onSuccess: (result) => {
      const queued = result.preview.filter(
        (p) => p.outcome === "queued",
      ).length;
      const skipped = result.preview.length - queued;
      if (queued === 0) toast.info("Nothing to do: all SIMs were skipped");
      else
        toast.success(
          `${queued} SIM${queued === 1 ? "" : "s"} queued${
            skipped ? `, ${skipped} skipped` : ""
          }`,
        );
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

type AssignResponse = {
  assigned: number;
  labelBatchId: string | null;
  labelPreview: PreviewEntry[];
};

export function useAssignSims() {
  return useMutation({
    mutationFn: (input: {
      simIds: string[];
      equipmentInstanceId: string;
      slot: number | null;
    }) => post<AssignResponse>("/api/sims/assign", input),
    onSuccess: (result) =>
      toast.success(
        `${result.assigned} SIM${result.assigned === 1 ? "" : "s"} assigned`,
      ),
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useUnassignSims() {
  return useMutation({
    mutationFn: (simIds: string[]) =>
      post<{ unassigned: number }>("/api/sims/unassign", { simIds }),
    onSuccess: (result) =>
      toast.success(
        `${result.unassigned} SIM${result.unassigned === 1 ? "" : "s"} unassigned`,
      ),
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useRefreshSims() {
  return useMutation({
    mutationFn: (
      input: { simIds: string[] } | { equipmentInstanceId: string },
    ) =>
      post<{ refreshed: number; cached: number }>("/api/sims/refresh", input),
    onSuccess: (result) =>
      toast.success(
        result.refreshed > 0
          ? `Refreshed ${result.refreshed} SIM${result.refreshed === 1 ? "" : "s"}`
          : "Already up to date (refreshed less than a minute ago)",
      ),
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useRefreshSimUsage() {
  return useMutation({
    mutationFn: (simIds: string[]) =>
      post<{ rows: number; errors: string[] }>("/api/sims/usage/refresh", {
        simIds,
      }),
    onSuccess: (result) =>
      result.errors.length > 0
        ? toast.error(`Usage refresh had errors: ${result.errors[0]}`)
        : toast.success("Usage history refreshed"),
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useSyncSims() {
  return useMutation({
    mutationFn: () =>
      post<{ devices: { fetched: number } }>("/api/sims/sync", {}),
    onSuccess: (result) =>
      toast.success(`Synced ${result.devices.fetched} SIMs from Simetric`),
    onError: (err: Error) => toast.error(err.message),
  });
}

export type ManualSimInput = {
  iccid: string;
  msisdn?: string | null;
  imsi?: string | null;
  imei?: string | null;
  label?: string | null;
  status?: "ACTIVATED" | "DEACTIVATED" | "INVENTORY";
};

export function useSaveManualSim() {
  return useMutation({
    mutationFn: ({
      id,
      ...input
    }: ManualSimInput & { id?: string }): Promise<unknown> =>
      id
        ? apiJson<{ ok: true }>(`/api/sims/manual/${id}`, {
            method: "PATCH",
            body: JSON.stringify(input),
          })
        : post<{ id: string }>("/api/sims/manual", input),
    onSuccess: () => toast.success("KPN SIM saved"),
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useDeleteManualSim() {
  return useMutation({
    mutationFn: (id: string) =>
      apiJson<{ ok: true }>(`/api/sims/manual/${id}`, { method: "DELETE" }),
    onSuccess: () => toast.success("KPN SIM deleted"),
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useUpdateSimLabel() {
  return useMutation({
    mutationFn: ({ id, label }: { id: string; label: string | null }) =>
      apiJson<{ ok: true }>(`/api/sims/cards/${id}/label`, {
        method: "PATCH",
        body: JSON.stringify({ label }),
      }),
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useUpdateSimNote() {
  return useMutation({
    mutationFn: ({ id, note }: { id: string; note: string | null }) =>
      apiJson<{ ok: true }>(`/api/sims/cards/${id}/note`, {
        method: "PATCH",
        body: JSON.stringify({ note }),
      }),
    onSuccess: () => toast.success("Note saved"),
    onError: (err: Error) => toast.error(err.message),
  });
}

export type SimProfileInput = {
  provider: SimProfile["provider"];
  name: string;
  isOpen: boolean;
  ratePlan: string | null;
  notes: string | null;
  networks: SimProfile["networks"];
};

export function useSaveSimProfile() {
  return useMutation({
    mutationFn: ({
      id,
      ...input
    }: SimProfileInput & { id?: string }): Promise<unknown> =>
      id
        ? apiJson<{ ok: true }>(`/api/sims/profiles/${id}`, {
            method: "PATCH",
            body: JSON.stringify(input),
          })
        : post<{ id: string }>("/api/sims/profiles", input),
    onSuccess: () => toast.success("Profile saved"),
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useDeleteSimProfile() {
  return useMutation({
    mutationFn: (id: string) =>
      apiJson<{ ok: true }>(`/api/sims/profiles/${id}`, { method: "DELETE" }),
    onSuccess: () => toast.success("Profile deleted"),
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useSetSimProfile() {
  return useMutation({
    mutationFn: (input: { simIds: string[]; profileId: string | null }) =>
      post<{ updated: number; skipped: { iccid: string; reason: string }[] }>(
        "/api/sims/profiles/assign",
        input,
      ),
    onSuccess: (result) => {
      const message = `Profile set on ${result.updated} SIM${result.updated === 1 ? "" : "s"}`;
      if (result.skipped.length > 0)
        toast.warning(
          `${message}; ${result.skipped.length} skipped: ${result.skipped[0].reason}`,
        );
      else toast.success(message);
    },
    onError: (err: Error) => toast.error(err.message),
  });
}
